<template>
    <div class="view-container">
        <div v-if="parametresType && isMobile" class="mobile-header-block">
          <div class="back-button-wrapper" @click="goBack('/analytics/reports')">
            <LeftOutlined class="back-button" />
          </div>
          <h2>{{ analyticsHeader ?? 'Аналитика' }}</h2>
        </div>
          <div v-if="parametresType && canAccessCurrentReport" class="analytic-form__options">
            <component
              @reportRequest="makeChart"
              v-bind:is="parametresType"
            ></component>
          </div>
          <div class="analytic-form__charts w-full">
            <div
              v-if="reportRequest && !reportData && !tempShowReport"
              class="
                w-full
                flex
                justify-content-center
                align-item-center
                preloader
              "
            >
              <preloader />
            </div>
            <template  v-else-if="reportData || tempShowReport">
              <div class="card">
                <component :groupId="groupId" v-bind:is="reportType"></component>
              </div>
              <div>
                <ReportNextStepPromotionCard :consultCommentProduct="analyticsHeaderDictionary[reportType]"/>
              </div>
            </template>
          </div>
    </div>
</template>

<script>
import { onBeforeMount, onBeforeUnmount, ref, watch } from "@vue/runtime-core";
import {useRoute, useRouter} from "vue-router";
import { computed } from "@vue/runtime-core";
import BaseReportParametres from "../../feature/analytics/report-parameters/BaseReportParametres.vue";
import BaseReport from "../../feature/analytics/reports/BaseReport.vue";
import Preloader from "@/components/Technical/Preloader.vue";
import GroupReportParametres from "../../feature/analytics/report-parameters/GroupReportParametres.vue";
import GroupReport from "../../feature/analytics/reports/GroupReport.vue";
import GeneralDynamicParametres from "../../feature/analytics/report-parameters/GeneralDynamicParametres.vue";
import ReferenceReportParametres from "../../feature/analytics/report-parameters/ReferenceReportParametres.vue";
import ReferenceUsersReportParametres from "../../feature/analytics/report-parameters/ReferenceUsersReportParametres.vue";
import RepeatedReportParametres from "../../feature/analytics/report-parameters/RepeatedReportParametres.vue";
import DemographicReportParametres from "../../feature/analytics/report-parameters/DemographicReportParametres.vue";
import BenchmarkParameters from "@/feature/analytics/report-parameters/BenchmarkParameters.vue";
import BenchmarkReport from "@/feature/analytics/reports/BenchmarkReport.vue";

import DemographicReport from "../../feature/analytics/reports/DemographicReport.vue";
import { useStore } from "vuex";
import ReferenceUsersReport from "../../feature/analytics/reports/ReferenceUsersReport.vue";
import SummaryTableParametres from "../../feature/analytics/report-parameters/SummaryTableParametres.vue";
import SummaryReport from "../../feature/analytics/reports/SummaryReport.vue";
import GeneralReport from "@/feature/analytics/reports/GeneralReport.vue";
import GeneralReportParameters from "@/feature/analytics/report-parameters/GeneralReportParameters.vue";
import TestingDataReportParameters from "@/feature/analytics/report-parameters/TestingDataReportParameters.vue";
import AnalyticsGroupsTags from "@/feature/analytics/charts/details/GroupTags.vue";
import {LeftOutlined, SearchOutlined} from "@ant-design/icons-vue";
import ReportNextStepPromotionCard from "@/components/Cards/ReportNextStepPromotionCard.vue";
import { message } from 'ant-design-vue';

import ManagementPotentialReport from "@/views/Report/ManagementPotentialReport/ManagementPotentialReport.vue";
import CompareReport from "@/views/Report/CompareReport/CompareReport.vue";
import GroupEiProfileParameters from "@/feature/analytics/report-parameters/GroupEiProfileParameters.vue";
import useCheckRights from "@/mixins/useCheckRights";
import analyticsReports from "@/mixins/dictionaries/analyticsReports";
import useBreakpoints from "@/mixins/useBreakpoints";
import {
  getReportAnalyticsAccessKey,
  hasSubscriptionAnalyticsAccess,
} from "@/utils/subscriptionAnalyticsAccess";

const reportsParamsDictionary = {
  group: "group-report-parametres",
  dynamic: "general-dynamic-parametres",
  reference: "reference-users-report-parametres",
  repeated: "group-ei-profile-parameters",
  benchmark: "benchmark-parameters",
  testing_data: "testing-data-report-parameters",
};

const reportsDictionary = {
  group: "compare-report",
  dynamic: "compare-report",
  reference: "reference-users-report",
  repeated: "management-potential-report",
  benchmark: "benchmark-report",
};

const analyticsHeaderDictionary = {
  group: "Сравнение групп",
  dynamic: "Аналитика изменений в ЭИ",
  repeated: "ЭИ-профиль группы",
  reference: "Эталонный профиль",
  benchmark: "Сравнение с бенчмарком по отрасли",
  testing_data: "Данные по тестированиям",
};

export default {
  components: {
    ReportNextStepPromotionCard,
    LeftOutlined,
    SearchOutlined,
    AnalyticsGroupsTags,
    BaseReportParametres,
    BaseReport,
    Preloader,
    GroupReportParametres,
    GroupReport,
    GeneralDynamicParametres,
    ReferenceReportParametres,
    ReferenceUsersReportParametres,
    RepeatedReportParametres,
    DemographicReportParametres,
    DemographicReport,
    ReferenceUsersReport,
    SummaryTableParametres,
    SummaryReport,
    GeneralReportParameters,
    GeneralReport,
    TestingDataReportParameters,
    BenchmarkParameters,
    BenchmarkReport,
    ManagementPotentialReport,
    CompareReport,
    GroupEiProfileParameters,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { getRight } = useCheckRights();
    const { name } = useRoute().params;
    const parametresType = ref(null),
      reportType = ref(null),
      reportRequest = ref(false),
      groupId = ref(null);
    const user = computed(() => store.state.profile.profile);
    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions);

    const analytics = analyticsReports;

    const reportData = computed(() => store.state.analytics.reportData),
      reportReservedData = computed(
        () => store.state.analytics.reportReservedData
      );
    const currentAnimation = ref("");
    const tempShowReport = ref(false);
    const hasAccessRedirected = ref(false);
    const isTestingDataReport = computed(() => name === 'testing_data');
    const canAccessCurrentReport = computed(() => {
      return !isTestingDataReport.value || getRight('superadmin');
    });
    const shouldCheckSubscription = computed(() => !['reference', 'repeated', 'testing_data'].includes(name));

    const makeChart = (data) => {
      store.commit("analytics/setReportData", null);
      groupId.value = data.group_id;

      switch (name) {
        case "group":
          store.dispatch("analytics/getGroupComparisonData", data);
          break;
        case "dynamic":
          store.dispatch("analytics/getGroupDynamicsData", data);
          break;
        case "repeated":
          store.dispatch("analytics/getGroupAverageData", data);
          break;
        case "reference":
          groupId.value = data.group_id;
          store.dispatch("analytics/getReferenceUsersData", data);
          break;
        case "benchmark":
          store.dispatch("analytics/getBenchmarkReport", data);
          break;
      }
      reportRequest.value = true;
    };

    const { isMobile } = useBreakpoints()

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back();
      } else {
        router.push(url);
      }
    };

    const redirectNoAccess = () => {
      if (hasAccessRedirected.value) {
        return;
      }

      hasAccessRedirected.value = true;
      message.info('Для продолжения требуется подписка');
      router.push({ name: 'analytics_reports' });
    };

    const loadPermittedSubscriptions = async () => {
      if (!permittedSubscriptions.value || !permittedSubscriptions.value.length) {
        if (getRight('subscription') && user.value?.id) {
          await store.dispatch('subscription/getPermittedSubscriptions', user.value?.company?.id ?? null);
        }
      }
    };

    const checkReportAccess = () => {
      let currentReport = analytics.general.find((el) => el.type === name);

      return currentReport ? currentReport.available : false;
    }

    const checkSubscriptionAccess = async () => {
      if (!user.value?.id) {
        return;
      }

      if (!canAccessCurrentReport.value) {
        if (!hasAccessRedirected.value) {
          hasAccessRedirected.value = true;
          router.push({ name: 'analytics_reports' });
        }
        return;
      }

      if (!shouldCheckSubscription.value) {
        return;
      }

      await loadPermittedSubscriptions();

      if (!checkReportAccess() && !hasSubscriptionAnalyticsAccess(permittedSubscriptions.value, getReportAnalyticsAccessKey(name))) {
        redirectNoAccess();
      }
    };

    const analyticsHeader = computed(() => store.state.analytics.analyticsHeader);

    onBeforeMount(() => {
      parametresType.value = reportsParamsDictionary[name];
      reportType.value = reportsDictionary[name];
      store.commit("analytics/setAnalyticsHeader", (analyticsHeaderDictionary[name]));
    });

    watch(
        () => [user.value?.id, user.value?.company?.id],
        () => {
          checkSubscriptionAccess();
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
      store.commit("analytics/setReportData", null);
      store.commit("analytics/setAnalyticsHeader", null);
    });

    return {
      parametresType,
      reportType,
      reportRequest,
      reportData,
      reportReservedData,
      makeChart,
      groupId,
      currentAnimation,
      isMobile,
      analyticsHeader,
      router,
      analyticsHeaderDictionary,
      tempShowReport,
      canAccessCurrentReport,
      goBack
    };
  },
};
</script>

<style scored lang="scss">
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s ease-out;
}

.slide-right-leave-to,
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(1000px);
}
.preloader {
  min-height: 200px;
}

.back-analytics {
  i {
    margin-right: 5px;
  }
}

.card-header {
  padding-bottom: 0;
}

.card-body {
  padding-top: 0;
}

.back-btn {
  display: flex;
  padding-bottom: 20px;
}

.hide {
  animation: slide-in-left 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53) reverse;
}
.show {
  animation: slide-in-left 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

@keyframes slide-in-left {
  0% {
    -webkit-transform: translateX(1000px);
    transform: translateX(1000px);
    opacity: 0;
  }
  65% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
  100% {
    opacity: 1;
  }
}

.analytic-form__options {
  .label {
    font-size: 14px;
    color: var(--grey-text);
    margin-bottom: 4px;
  }
}

.ant-alert-message p {
  margin-bottom: 0 !important;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #FFFFFF;
  border-radius: 25%;
  cursor: pointer;
}

.mobile-header-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: flex-start;
  }
}

.mobile-header-block h2 {
  font-size: 20px;
  margin-bottom: 0;
}
</style>
